import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, addDays, differenceInDays } from "date-fns";
import { Tag, Check, X, Phone, ChevronLeft, ChevronRight, Minus, Calendar as CalendarIcon, Users, CreditCard, ArrowRight, ArrowLeft, CheckCircle2, MapPin, Star, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Max limits
const MAX_TOTAL_MEMBERS = 15;
const MAX_ADULTS = 10;
const MAX_CHILDREN = 5;

export default function Bookings() {
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  
  // Dynamic pricing
  const [pricingBreakdown, setPricingBreakdown] = useState(null);
  
  // Guest counts
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  
  // Discount
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  
  // Guest information
  const [formData, setFormData] = useState({
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    notes: ""
  });

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarFocused, setCalendarFocused] = useState(false);
  const [showDropdownCalendar, setShowDropdownCalendar] = useState(false);
  const calendarRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await axios.get(`${API}/rooms`);
        console.log("Rooms fetched:", res.data);
        console.log("First room blocked_dates:", res.data[0]?.blocked_dates);
        setRooms(res.data);
        if (res.data.length > 0) {
          setSelectedRoom(res.data[0]);
        }
      } catch (error) {
        console.error("Error fetching rooms:", error);
        toast.error("Failed to load rooms");
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, []);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedRoom) return;
      
      console.log("Selected room blocked_dates:", selectedRoom.blocked_dates);
      
      try {
        const month1 = format(currentMonth, "yyyy-MM");
        const nextMonth = new Date(currentMonth);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        const month2 = format(nextMonth, "yyyy-MM");
        
        const [res1, res2] = await Promise.all([
          axios.get(`${API}/bookings/availability/${selectedRoom.id}?month=${month1}`),
          axios.get(`${API}/bookings/availability/${selectedRoom.id}?month=${month2}`)
        ]);
        
        console.log("Availability API response 1:", res1.data);
        console.log("Availability API response 2:", res2.data);
        
        // Handle blocked_dates from booking availability API (may not exist)
        const blocked = [
          ...(Array.isArray(res1.data.blocked_dates) ? res1.data.blocked_dates : []),
          ...(Array.isArray(res2.data.blocked_dates) ? res2.data.blocked_dates : [])
        ];
        
        console.log("Booking blocked dates:", blocked);
        
        // Add blocked dates from room model
        const roomBlockedDates = [];
        if (selectedRoom.blocked_dates && selectedRoom.blocked_dates.length > 0) {
          console.log("Processing room blocked dates:", selectedRoom.blocked_dates);
          selectedRoom.blocked_dates.forEach(bd => {
            const fromDate = new Date(bd.from_date);
            const toDate = bd.to_date ? new Date(bd.to_date) : fromDate;
            
            console.log(`Block range: ${format(fromDate, "yyyy-MM-dd")} to ${format(toDate, "yyyy-MM-dd")}`);
            
            // Add all dates in the range
            const currentDate = new Date(fromDate);
            while (currentDate <= toDate) {
              const dateStr = format(currentDate, "yyyy-MM-dd");
              roomBlockedDates.push(dateStr);
              console.log("Added blocked date:", dateStr);
              currentDate.setDate(currentDate.getDate() + 1);
            }
          });
        } else {
          console.log("No room blocked dates found");
        }
        
        // Combine blocked dates from bookings and room model
        const allBlockedDates = [
          ...blocked.map(d => format(new Date(d), "yyyy-MM-dd")),
          ...roomBlockedDates
        ];
        
        // Convert to Date objects and remove duplicates
        const uniqueDates = [...new Set(allBlockedDates)];
        setBlockedDates(uniqueDates.map(d => new Date(d)));
        
        console.log("Blocked dates set:", uniqueDates);
      } catch (error) {
        console.error("Error fetching availability:", error);
      }
    };
    fetchAvailability();
  }, [selectedRoom, currentMonth]);

  const isDateBlocked = (date) => {
    const isBlocked = blockedDates.some(
      blocked => format(blocked, "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
    );
    if (isBlocked) {
      console.log("Date blocked:", format(date, "yyyy-MM-dd"));
    }
    return isBlocked;
  };

  const calculateNights = () => {
    if (!checkInDate || !checkOutDate) return 0;
    return differenceInDays(checkOutDate, checkInDate);
  };

  // Fetch dynamic pricing when dates or guest count changes
  useEffect(() => {
    const fetchPricing = async () => {
      if (!selectedRoom || !checkInDate || !checkOutDate) {
        setPricingBreakdown(null);
        return;
      }

      try {
        const res = await axios.get(
          `${API}/rooms/${selectedRoom.id}/pricing`,
          {
            params: {
              check_in_date: format(checkInDate, "yyyy-MM-dd"),
              check_out_date: format(checkOutDate, "yyyy-MM-dd"),
              num_adults: adults,
              num_children: children
            }
          }
        );
        setPricingBreakdown(res.data);
      } catch (error) {
        console.error("Error fetching pricing:", error);
        toast.error("Failed to calculate pricing");
        setPricingBreakdown(null);
      }
    };

    fetchPricing();
  }, [selectedRoom, checkInDate, checkOutDate, adults, children]);

  const calculateSubtotal = () => {
    if (!pricingBreakdown) return 0;
    return pricingBreakdown.total_amount;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    if (discountApplied) {
      return discountApplied.final_amount;
    }
    return subtotal;
  };

  // Helper to get base price for display - always show the higher price
  const getBasePrice = () => {
    if (!selectedRoom) return 0;
    
    const defaultPrice = selectedRoom.default_price_per_night || selectedRoom.price_per_night || 0;
    
    // If dates are selected, check if they fall within seasonal period
    if (checkInDate && selectedRoom.seasonal_pricing && selectedRoom.seasonal_pricing.length > 0) {
      for (const seasonal of selectedRoom.seasonal_pricing) {
        const fromDate = new Date(seasonal.from_date);
        fromDate.setHours(0, 0, 0, 0);
        const toDate = new Date(seasonal.to_date);
        toDate.setHours(23, 59, 59, 999);
        const checkIn = new Date(checkInDate);
        checkIn.setHours(0, 0, 0, 0);
        
        // If check-in date falls within seasonal period, return the HIGHER price
        if (checkIn >= fromDate && checkIn <= toDate) {
          const seasonalPrice = seasonal.price_per_night;
          return Math.max(defaultPrice, seasonalPrice);
        }
      }
    }
    
    // Otherwise show default price
    return defaultPrice;
  };

  const getMaxGuests = () => {
    return selectedRoom?.max_guests || 6;
  };

  // Handle adult count change with modal check
  const handleAdultChange = (value) => {
    const numAdults = parseInt(value);
    if (numAdults > MAX_ADULTS) {
      setShowContactModal(true);
      return;
    }
    setAdults(numAdults);
    // Reset discount when guest count changes
    setDiscountApplied(null);
    setDiscountCode("");
  };

  const handleChildrenChange = (value) => {
    const numChildren = parseInt(value);
    setChildren(numChildren);
    // Reset discount when guest count changes
    setDiscountApplied(null);
    setDiscountCode("");
  };

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      toast.error("Please enter a discount code");
      return;
    }

    const subtotal = calculateSubtotal();
    if (subtotal <= 0) {
      toast.error("Please select check-in and check-out dates first");
      return;
    }

    setValidatingDiscount(true);
    try {
      const res = await axios.post(`${API}/discount/validate?code=${discountCode}&amount=${subtotal}`);
      setDiscountApplied(res.data);
      toast.success(`Discount code applied! You save ₹${res.data.discount_amount.toLocaleString()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid discount code");
      setDiscountApplied(null);
    } finally {
      setValidatingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setDiscountCode("");
    setDiscountApplied(null);
    toast.info("Discount code removed");
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!checkInDate || !checkOutDate) {
      toast.error("Please select check-in and check-out dates");
      return;
    }

    if (!formData.guest_name || !formData.guest_email || !formData.guest_phone) {
      toast.error("Please fill all required fields");
      return;
    }

    if (adults > MAX_ADULTS) {
      setShowContactModal(true);
      return;
    }

    setBookingLoading(true);
    try {
      const res = await axios.post(`${API}/bookings/create`, {
        room_id: selectedRoom.id,
        guest_name: formData.guest_name,
        guest_email: formData.guest_email,
        guest_phone: formData.guest_phone,
        check_in_date: format(checkInDate, "yyyy-MM-dd"),
        check_out_date: format(checkOutDate, "yyyy-MM-dd"),
        num_guests: adults + children,
        num_adults: adults,
        num_children: children,
        discount_code: discountApplied ? discountApplied.code : null
      });

      // Initialize Razorpay payment
      const options = {
        key: res.data.keyId,
        amount: res.data.amount * 100, // Amount in paise
        currency: res.data.currency,
        name: "Wyldwoods Hotel",
        description: `Booking for ${selectedRoom.name}`,
        order_id: res.data.orderId,
        handler: async function (response) {
          setVerifyingPayment(true);
          try {
            // Verify payment
            const verifyRes = await axios.post(`${API}/bookings/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            if (verifyRes.data.success) {
              toast.success("Booking confirmed successfully!");
              // Redirect to success page immediately
              window.location.href = `/booking/success?booking_id=${verifyRes.data.bookingId}`;
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            toast.error("Payment verification failed");
            setVerifyingPayment(false);
            setBookingLoading(false);
          }
        },
        prefill: {
          name: formData.guest_name,
          email: formData.guest_email,
          contact: formData.guest_phone
        },
        theme: {
          color: "#4F46E5"
        },
        modal: {
          ondismiss: function() {
            setBookingLoading(false);
            toast.error("Payment cancelled");
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setBookingLoading(false);
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.message || error.response?.data?.detail || "Failed to create booking");
    } finally {
      setBookingLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    
    if (!checkInDate || (checkInDate && checkOutDate)) {
      // Starting fresh or resetting
      setCheckInDate(date);
      setCheckOutDate(null);
      setDiscountApplied(null);
      setCurrentMonth(date);
    } else {
      // Check-in is set, now setting check-out
      if (date > checkInDate) {
        // Validate that no dates in the range are blocked
        const hasBlockedDatesInRange = checkForBlockedDatesInRange(checkInDate, date);
        
        if (hasBlockedDatesInRange) {
          toast.error("Selected date range contains blocked dates. Please choose different dates.");
          return;
        }
        
        setCheckOutDate(date);
        setDiscountApplied(null);
        // Close dropdown calendar after selecting checkout date
        setTimeout(() => setShowDropdownCalendar(false), 300);
      } else {
        // User selected earlier date, reset to new check-in
        setCheckInDate(date);
        setCheckOutDate(null);
        setDiscountApplied(null);
        setCurrentMonth(date);
      }
    }
  };
  
  // Helper function to check if any dates in range are blocked
  const checkForBlockedDatesInRange = (startDate, endDate) => {
    if (!startDate || !endDate) return false;
    
    const current = new Date(startDate);
    const end = new Date(endDate);
    
    // Check each night being stayed (excluding checkout date)
    while (current < end) {
      if (isDateBlocked(current)) {
        console.log("Found blocked date in range:", format(current, "yyyy-MM-dd"));
        return true;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return false;
  };

  const scrollToCalendar = () => {
    setCalendarFocused(true);
    setTimeout(() => {
      calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Remove focus effect after 2 seconds
      setTimeout(() => setCalendarFocused(false), 2000);
    }, 100);
  };

  const toggleDropdownCalendar = () => {
    setShowDropdownCalendar(!showDropdownCalendar);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdownCalendar(false);
      }
    };

    if (showDropdownCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdownCalendar]);

  const isDateInRange = (date) => {
    if (!checkInDate || !checkOutDate) return false;
    return date > checkInDate && date < checkOutDate;
  };
  
  // Check if a date in range is blocked (for visual indication)
  const isBlockedDateInSelectedRange = (date) => {
    if (!checkInDate || !checkOutDate) return false;
    const inRange = date > checkInDate && date < checkOutDate;
    return inRange && isDateBlocked(date);
  };

  const clearDates = () => {
    setCheckInDate(null);
    setCheckOutDate(null);
    setDiscountApplied(null);
    setDiscountCode("");
  };

  const handleContinueToStep2 = () => {
    if (!checkInDate || !checkOutDate) {
      toast.error("Please select check-in and check-out dates");
      return;
    }
    if (calculateNights() < 1) {
      toast.error("Please select valid dates");
      return;
    }
    
    // Final validation: Check if any dates in range are blocked
    const hasBlockedDatesInRange = checkForBlockedDatesInRange(checkInDate, checkOutDate);
    if (hasBlockedDatesInRange) {
      toast.error("Selected date range contains unavailable dates. Please choose different dates.");
      // Reset dates to force re-selection
      setCheckOutDate(null);
      return;
    }
    
    // Scroll to top first, then change step
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setCurrentStep(2);
    }, 100);
  };

  const handleContinueToStep3 = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setCurrentStep(3);
    }, 100);
  };

  const handleBackToStep1 = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setCurrentStep(1);
    }, 100);
  };

  const handleBackToStep2 = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setCurrentStep(2);
    }, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#E67E22] border-t-transparent rounded-full animate-spin mb-4 mx-auto" />
          <p className="text-gray-600 text-lg">Loading your perfect stay...</p>
        </div>
      </div>
    );
  }

  const nights = calculateNights();

  // Step indicators
  const steps = [
    { number: 1, title: "Check Availability", icon: CalendarIcon },
    { number: 2, title: "Choose Room", icon: MapPin },
    { number: 3, title: "Guest Details", icon: CreditCard }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="bookings-page">
      <Header />
      
      {/* Only show banner on Step 1 */}
      {currentStep === 1 && (
        <PageBanner
          title="BOOK YOUR STAY"
          breadcrumbs={[
            { name: "HOME", path: "/" },
            { name: "BOOKING" }
          ]}
          backgroundImage="https://images.pexels.com/photos/1509443/pexels-photo-1509443.jpeg"
        />
      )}

      <section className={currentStep === 1 ? "py-6 px-4" : "py-6 px-4 pt-20"}>
        <div className="max-w-7xl mx-auto">
          
          {/* Compact Step Indicator */}
          <div className="mb-6 bg-gray-50 py-4">
            <div className="flex items-center justify-center relative">
              {steps.map((step, idx) => (
                <React.Fragment key={step.number}>
                  <div className="flex items-center gap-2 relative z-10">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-semibold transition-all shadow-md ${
                      currentStep >= step.number 
                        ? 'bg-[#E67E22] text-white' 
                        : 'bg-white border-2 border-gray-300 text-gray-400'
                    }`}>
                      {currentStep > step.number ? <Check size={16} /> : step.number}
                    </div>
                    <span className={`text-sm font-medium hidden md:inline ${currentStep >= step.number ? 'text-gray-700' : 'text-gray-400'}`}>
                      {step.title}
                    </span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`w-12 md:w-20 h-1 mx-2 transition-all rounded-full relative z-0 ${
                      currentStep > step.number ? 'bg-[#E67E22]' : 'bg-gray-300'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step 1: Check Availability - Compact Layout */}
          {currentStep === 1 && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-[#E67E22] px-6 py-3 text-white">
                <h2 className="font-semibold text-lg flex items-center gap-2">
                  <CalendarIcon size={20} />
                  Check Availability
                </h2>
              </div>

              <div className="p-6">
                {/* Compact 2-Column Layout */}
                <div className="grid lg:grid-cols-2 gap-6">
                  
                  {/* Left: Guest & Date Selection */}
                  <div className="space-y-4">
                    {/* Guest Selection - Compact */}
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2 text-sm">
                        <Users size={16} className="text-[#E67E22]" />
                        Guests
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-medium text-gray-600 mb-1 block">
                            Adults <span className="text-red-500">*</span>
                          </Label>
                          <Select value={adults.toString()} onValueChange={handleAdultChange}>
                            <SelectTrigger className="h-10 text-sm" data-testid="adults-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[...Array(MAX_ADULTS + 1)].map((_, i) => (
                                <SelectItem key={i} value={(i + 1).toString()}>
                                  {i + 1} Adult{i !== 0 ? 's' : ''}
                                </SelectItem>
                              ))}
                              <SelectItem value="11" className="text-gray-500">
                                More than 10
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label className="text-xs font-medium text-gray-600 mb-1 block">
                            Children (≤9)
                          </Label>
                          <Select value={children.toString()} onValueChange={handleChildrenChange}>
                            <SelectTrigger className="h-10 text-sm" data-testid="children-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[...Array(MAX_CHILDREN + 1)].map((_, i) => (
                                <SelectItem key={i} value={i.toString()}>
                                  {i} Child{i !== 1 ? 'ren' : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="mt-2 px-3 py-1.5 bg-blue-50 rounded text-xs text-blue-700 flex items-center gap-1">
                        <Users size={12} />
                        <span>Total: {adults + children} guest{(adults + children) !== 1 ? 's' : ''}</span>
                        {selectedRoom && adults > getMaxGuests() && (
                          <span className="text-blue-600">({adults - getMaxGuests()} extra adults)</span>
                        )}
                      </div>
                    </div>

                    {/* Date Input Boxes - Interactive */}
                    <div className="relative">
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2 text-sm">
                        <CalendarIcon size={16} className="text-[#E67E22]" />
                        Dates
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-medium text-gray-600 mb-1 block">
                            Check-in
                          </Label>
                          <div 
                            onClick={scrollToCalendar}
                            className="border border-gray-300 rounded-md px-3 py-2 bg-white hover:border-[#E67E22] transition-colors cursor-pointer h-10 flex items-center justify-between"
                          >
                            {checkInDate ? (
                              <span className="text-sm text-gray-800">{format(checkInDate, "MMM d, yyyy")}</span>
                            ) : (
                              <span className="text-sm text-gray-400 italic">Select date</span>
                            )}
                            <CalendarIcon size={16} className="text-gray-400" />
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs font-medium text-gray-600 mb-1 block">
                            Check-out
                          </Label>
                          <div 
                            onClick={scrollToCalendar}
                            className="border border-gray-300 rounded-md px-3 py-2 bg-white hover:border-[#E67E22] transition-colors cursor-pointer h-10 flex items-center justify-between"
                          >
                            {checkOutDate ? (
                              <span className="text-sm text-gray-800">{format(checkOutDate, "MMM d, yyyy")}</span>
                            ) : (
                              <span className="text-sm text-gray-400 italic">Select date</span>
                            )}
                            <CalendarIcon size={16} className="text-gray-400" />
                          </div>
                        </div>
                      </div>

                      {nights > 0 && (
                        <div className="mt-2 px-3 py-1.5 bg-purple-50 rounded text-xs font-semibold text-purple-700 text-center">
                          {nights} night{nights !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: Calendar - Always visible */}
                  <div ref={calendarRef}>
                    <div className={`bg-gray-50 rounded-lg p-3 border-2 transition-all duration-300 ${
                      calendarFocused ? 'border-[#E67E22] shadow-lg ring-2 ring-orange-200' : 'border-gray-200'
                    }`}>
                      <div className="flex justify-center overflow-x-auto">
                        <Calendar
                          mode="single"
                          selected={checkInDate}
                          onSelect={handleDateSelect}
                          numberOfMonths={2}
                          month={currentMonth}
                          onMonthChange={setCurrentMonth}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const maxDate = addDays(today, 90);

                            if (date < today || date > maxDate) return true;

                            // Hotel policy: checkout 10 AM, next check-in 12 PM.
                            // When the user is selecting a checkout date after already
                            // choosing a check-in, allow a blocked date as checkout
                            // only if no blocked night falls between check-in and it.
                            // e.g. Apr 3 check-in → Apr 4 checkout is valid even though
                            // Apr 4 is another booking's check-in (they arrive at 12 PM,
                            // this guest leaves at 10 AM).
                            if (checkInDate && !checkOutDate && date > checkInDate && isDateBlocked(date)) {
                              return checkForBlockedDatesInRange(checkInDate, date);
                            }

                            return isDateBlocked(date);
                          }}
                          className="rounded-md"
                          modifiers={{
                            booked_start: checkInDate,
                            booked_end: checkOutDate,
                            booked_range: (date) => isDateInRange(date),
                            selected: (date) => checkInDate && date.toDateString() === checkInDate.toDateString(),
                          }}
                          modifiersClassNames={{
                            booked_start: "!bg-[#E67E22] !text-white hover:!bg-[#D35400] font-bold",
                            booked_end: "!bg-[#E67E22] !text-white hover:!bg-[#D35400] font-bold",
                            booked_range: "bg-orange-100 text-orange-900",
                            selected: "!bg-[#E67E22] !text-white hover:!bg-[#D35400]",
                          }}
                          classNames={{
                            day_selected: "bg-[#E67E22] text-white hover:bg-[#D35400] !important",
                            day_today: "bg-blue-100 text-blue-800 font-bold border border-blue-400",
                            day_disabled: "text-gray-300 line-through",
                            months: "flex gap-4 flex-col md:flex-row",
                          }}
                          data-testid="booking-calendar"
                        />
                      </div>
                      <div className="flex justify-between items-center text-xs pt-2 border-t border-gray-200 mt-2">
                        <div className="flex gap-3">
                          <span className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
                            Today
                          </span>
                          <span className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-[#E67E22] rounded"></div>
                            Selected
                          </span>
                          {checkInDate && checkOutDate && (
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-orange-100 border border-orange-200 rounded"></div>
                              Range
                            </span>
                          )}
                        </div>
                        <button 
                          onClick={clearDates}
                          className="text-[#E67E22] hover:text-[#D35400] font-medium"
                          data-testid="clear-dates-btn"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Continue Button */}
                <div className="flex justify-end mt-4">
                  <Button
                    onClick={handleContinueToStep2}
                    disabled={!checkInDate || !checkOutDate || nights < 1}
                    className="bg-[#E67E22] hover:bg-[#D35400] text-white px-8 py-2.5 text-sm font-semibold disabled:opacity-50"
                  >
                    Continue
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Choose Room & Pricing - Compact */}
          {currentStep === 2 && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-[#E67E22] px-6 py-3 text-white">
                <h2 className="font-semibold text-lg flex items-center gap-2">
                  <MapPin size={20} />
                  Room & Pricing
                </h2>
              </div>

              <div className="p-6">
                {/* Compact Summary */}
                <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <div className="text-blue-600 font-medium">Check-in</div>
                      <div className="text-gray-700 font-semibold">{checkInDate && format(checkInDate, "MMM d")}</div>
                    </div>
                    <div>
                      <div className="text-blue-600 font-medium">Check-out</div>
                      <div className="text-gray-700 font-semibold">{checkOutDate && format(checkOutDate, "MMM d")}</div>
                    </div>
                    <div>
                      <div className="text-blue-600 font-medium">Guests</div>
                      <div className="text-gray-700 font-semibold">{adults}A, {children}C</div>
                    </div>
                    <div>
                      <div className="text-blue-600 font-medium">Nights</div>
                      <div className="text-gray-700 font-semibold">{nights}</div>
                    </div>
                  </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                  
                  {/* Left/Center: Room & Pricing */}
                  <div className="lg:col-span-2 space-y-4">
                    
                    {/* Room Card - Compact */}
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2 text-sm">Available Room</h3>
                      <div className="border border-gray-200 rounded-lg p-4 hover:border-[#E67E22] transition-colors">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-800 text-base mb-1">
                              {selectedRoom?.name || "Standard Room"}
                            </h4>
                            <div className="flex items-center gap-1 text-amber-500 mb-2">
                              {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
                            </div>
                            <p className="text-xs text-gray-600 mb-2">
                              Premium accommodation • Up to {getMaxGuests()} guests • Free WiFi
                            </p>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-xs text-gray-500">from</div>
                            <div className="text-xl font-bold text-[#E67E22]">₹{getBasePrice().toLocaleString()}</div>
                            <div className="text-xs text-gray-500">per night</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Price Breakdown - Compact */}
                    <div data-testid="price-breakdown">
                      <h3 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-1">
                        <Tag size={14} className="text-[#E67E22]" />
                        Price Details
                      </h3>
                      <div className="border border-gray-200 rounded-lg p-4 space-y-2 text-sm">
                        {pricingBreakdown ? (
                          <>
                            {/* Show detailed breakdown */}
                            {pricingBreakdown.breakdown && pricingBreakdown.breakdown.map((day, idx) => (
                              <div key={idx} className="space-y-1 pb-2 border-b border-gray-100">
                                {/* <div className="flex justify-between text-xs text-gray-500">
                                  <span>{new Date(day.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</span>
                                  {day.is_seasonal && <span className="text-orange-600 font-medium">Seasonal</span>}
                                </div> */}
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Base rate</span>
                                  <span className="font-medium">₹{day.base_rate.toLocaleString()}</span>
                                </div>
                                {day.extra_adults > 0 && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Extra adults ({day.extra_adults} × ₹{day.price_per_adult.toLocaleString()})</span>
                                    <span className="text-gray-600">₹{(day.extra_adults * day.price_per_adult).toLocaleString()}</span>
                                  </div>
                                )}
                                {day.children_count > 0 && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Children ({day.children_count} × ₹{day.price_per_children.toLocaleString()})</span>
                                    <span className="text-gray-600">₹{(day.children_count * day.price_per_children).toLocaleString()}</span>
                                  </div>
                                )}
                                <div className="flex justify-between font-medium text-[#E67E22]">
                                  <span>Day total</span>
                                  <span>₹{day.day_total.toLocaleString()}</span>
                                </div>
                              </div>
                            ))}

                            <div className="flex justify-between pt-2 border-t-2">
                              <span className="text-gray-600">Number of nights</span>
                              <span className="font-medium">×{nights}</span>
                            </div>

                            <div className="flex justify-between py-2 bg-orange-50 -mx-4 px-4 rounded">
                              <span className="font-semibold">Subtotal</span>
                              <span className="font-bold">₹{pricingBreakdown.total_amount.toLocaleString()}</span>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-4 text-gray-500 text-xs">
                            Select dates to see pricing
                          </div>
                        )}

                        {discountApplied && (
                          <div className="flex justify-between text-green-600">
                            <span>Discount ({discountApplied.code})</span>
                            <span className="font-semibold">-₹{discountApplied.discount_amount.toLocaleString()}</span>
                          </div>
                        )}

                        {pricingBreakdown && (
                          <div className="flex justify-between py-3 bg-[#E67E22] text-white -mx-4 px-4 rounded font-bold text-base">
                            <span>TOTAL</span>
                            <span>₹{calculateTotal().toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Discount Code */}
                  <div className="lg:col-span-1">
                    <h3 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-1">
                      <Sparkles size={14} className="text-[#E67E22]" />
                      Coupon Code
                    </h3>
                    
                    {!discountApplied ? (
                      <div className="space-y-2">
                        <div className="relative">
                          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                          <Input
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                            placeholder="Enter code"
                            className="pl-10 h-10 text-sm"
                            data-testid="discount-code-input"
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={applyDiscountCode}
                          disabled={validatingDiscount}
                          className="w-full bg-[#E67E22] hover:bg-[#D35400] text-white h-10 text-sm"
                          data-testid="apply-discount-btn"
                        >
                          {validatingDiscount ? "..." : "Apply Code"}
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-green-50 p-3 rounded-lg border border-green-300">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle2 size={16} />
                            <span className="font-semibold text-sm">{discountApplied.code}</span>
                          </div>
                          <button
                            type="button"
                            onClick={removeDiscount}
                            className="text-red-500 hover:text-red-700"
                            data-testid="remove-discount-btn"
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <div className="text-xs text-green-600">
                          Saved ₹{discountApplied.discount_amount.toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex justify-between mt-6 pt-4 border-t">
                  <Button
                    onClick={handleBackToStep1}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2.5 text-sm"
                  >
                    <ArrowLeft className="mr-2" size={16} />
                    Back
                  </Button>
                  <Button
                    onClick={handleContinueToStep3}
                    className="bg-[#E67E22] hover:bg-[#D35400] text-white px-8 py-2.5 text-sm font-semibold"
                  >
                    Continue
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Guest Information & Payment - Compact */}
          {currentStep === 3 && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-[#E67E22] px-6 py-3 text-white">
                <h2 className="font-semibold text-lg flex items-center gap-2">
                  <CreditCard size={20} />
                  Guest Details & Payment
                </h2>
              </div>

              <div className="p-6">
                <div className="grid lg:grid-cols-3 gap-6">
                  
                  {/* Left/Center: Guest Form */}
                  <div className="lg:col-span-2 space-y-4">
                    
                    {/* Guest Information */}
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-3 text-sm">Guest Information</h3>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs font-medium text-gray-600 mb-1 block">
                            Full Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            value={formData.guest_name}
                            onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                            placeholder="Enter full name"
                            className="h-10 text-sm"
                            data-testid="guest-name-input"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs font-medium text-gray-600 mb-1 block">
                              Email <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              type="email"
                              value={formData.guest_email}
                              onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                              placeholder="email@example.com"
                              className="h-10 text-sm"
                              data-testid="guest-email-input"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-xs font-medium text-gray-600 mb-1 block">
                              Phone <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              type="tel"
                              value={formData.guest_phone}
                              onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                              placeholder="+91 1234567890"
                              className="h-10 text-sm"
                              data-testid="guest-phone-input"
                            />
                          </div>
                        </div>

                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <Label className="text-xs font-medium text-gray-600">
                              Special Requests (Optional)
                            </Label>
                            <span className={`text-xs ${formData.notes.length > 180 ? 'text-orange-600 font-medium' : 'text-gray-400'}`}>
                              {formData.notes.length}/200
                            </span>
                          </div>
                          <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            className="w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-[#E67E22] focus:border-[#E67E22] text-sm"
                            rows={3}
                            maxLength={200}
                            placeholder="Any special requests..."
                            data-testid="notes-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Payment Info */}
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                          <CreditCard className="text-white" size={16} />
                        </div>
                        <div>
                          <div className="font-semibold text-green-900 text-sm">Secure Payment</div>
                          <div className="text-green-700 text-xs">Powered by Razorpay</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Booking Summary */}
                  <div className="lg:col-span-1">
                    <h3 className="font-semibold text-gray-800 mb-3 text-sm">Booking Summary</h3>
                    <div className="border border-gray-200 rounded-lg p-4 space-y-3 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Check-in</span>
                        <span className="font-medium">{checkInDate && format(checkInDate, "MMM d, yyyy")}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Check-out</span>
                        <span className="font-medium">{checkOutDate && format(checkOutDate, "MMM d, yyyy")}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Nights</span>
                        <span className="font-medium">{nights}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Guests</span>
                        <span className="font-medium">{adults} Adults, {children} Children</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Room</span>
                        <span className="font-medium">Standard</span>
                      </div>
                      <div className="flex justify-between pt-3 border-t text-base font-bold text-[#E67E22]">
                        <span>Total</span>
                        <span>₹{calculateTotal().toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Validation Warning */}
                {adults > MAX_ADULTS && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-300 rounded-lg text-red-700 text-xs">
                    <div className="flex items-center gap-2 font-semibold mb-1">
                      <X size={14} />
                      Invalid Guest Count
                    </div>
                    <p>Online booking allowed up to 10 adults only.</p>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between mt-6 pt-4 border-t">
                  <Button
                    onClick={handleBackToStep2}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2.5 text-sm"
                  >
                    <ArrowLeft className="mr-2" size={16} />
                    Back
                  </Button>
                  <Button
                    onClick={handleBooking}
                    disabled={bookingLoading || !formData.guest_name || !formData.guest_email || !formData.guest_phone}
                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-2.5 text-sm font-semibold disabled:opacity-50"
                    data-testid="book-now-btn"
                  >
                    {bookingLoading ? (
                      <>
                        <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2" size={16} />
                        Complete Booking
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* Contact Modal for More Than 10 Adults */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-md bg-white z-[100]" data-testid="contact-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2C3E50]">
              Contact Us for Large Groups
            </DialogTitle>
          </DialogHeader>
          <div className="text-[#7F8C8D] space-y-4">
            <p>
              Online booking is allowed only up to <strong>10 adults</strong> or a maximum of <strong>15 total guests</strong>.
            </p>
            <p>
              For groups exceeding these limits, please contact us directly:
            </p>
            <div className="flex items-center justify-center gap-3 py-4">
              <Phone className="text-[#E67E22]" size={24} />
              <a 
                href="tel:+919445771282" 
                className="text-2xl font-bold text-[#E67E22] hover:underline"
              >
                +91 9445771282
              </a>
            </div>
            <p className="text-sm text-center">
              Our team will help you with custom arrangements for your group.
            </p>
          </div>
          <div className="flex justify-center pt-4">
            <Button
              onClick={() => {
                setShowContactModal(false);
                // Reset to safe values
                const safeAdults = Math.min(adults, MAX_ADULTS);
                const safeChildren = Math.min(children, MAX_TOTAL_MEMBERS - safeAdults);
                setAdults(safeAdults);
                setChildren(safeChildren);
              }}
              className="bg-[#E67E22] hover:bg-[#D35400] text-white"
            >
              Adjust Guest Count
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
      
      {/* Payment Verification Loading Overlay */}
      {verifyingPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center">
            <div className="w-16 h-16 border-4 border-[#E67E22] border-t-transparent rounded-full animate-spin mb-4 mx-auto" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Verifying Payment...</h3>
            <p className="text-gray-600 text-sm">Please wait while we confirm your booking</p>
          </div>
        </div>
      )}
    </div>
  );
}
